/**
 * Представление загрузки одной машины для МСК Газобетона.
 *
 * Единственное место, где решается, сколько мест занято и сколько свободно.
 * Им пользуются и карточки машин (buildGazVehicleLoadModel), и подпись блока КП
 * (buildGazCommercialProposalBlocks), поэтому разойтись они больше не могут.
 *
 * Здесь нет норм погрузки, выбора транспорта и стоимости: вместимость и загрузка
 * приходят готовыми из серверного deliveryBreakdown.
 */

const round3 = (value) => Math.round((Number(value) || 0) * 1000) / 1000;

const loadPercentOf = (loadedPallets, capacity) => {
  if (!(capacity > 0)) {
    return 0;
  }
  const ratio = Math.min(Math.max(loadedPallets / capacity, 0), 1);
  return Math.round(ratio * 100);
};

/**
 * @param {object} params
 * @param {number} params.capacity            — вместимость машины в паллетоместах
 * @param {number} params.loadedPallets       — занятые паллетоместа
 * @param {number} [params.palletVolume]      — кубов на поддоне, только для показа объёмов
 * @param {boolean} [params.hasUnmeasuredCargo] — в машине есть груз без нормы поддона
 */
export const buildGazVehicleLoadView = ({
  capacity,
  loadedPallets,
  palletVolume,
  hasUnmeasuredCargo = false,
} = {}) => {
  const safeCapacity = Number(capacity) || 0;
  const safeLoaded = Math.max(Number(loadedPallets) || 0, 0);
  const cappedLoaded = safeCapacity > 0 ? Math.min(safeLoaded, safeCapacity) : safeLoaded;

  // Поддоны приходят приведёнными к единой кубатуре, поэтому позиция с менее объёмным
  // поддоном даёт дробь: 7 поддонов газобетона + 1 поддон U-блока = 7,625. Физически
  // занято 8 паллетомест из 8. Дробный остаток занять нечем — доложить можно только
  // целый поддон, поэтому свободные места округляем вниз, а занятые добираем до нормы.
  const rawFreePallets = safeCapacity > 0 ? Math.max(round3(safeCapacity - cappedLoaded), 0) : 0;
  const freePalletsByNumbers = Math.floor(rawFreePallets);

  // Груз без нормы поддона физически место занимает, но посчитать его нечем.
  // Обещать свободную вместимость в такой машине нельзя — показываем ноль и
  // не считаем машину полной, чтобы её не приняли за закрытую.
  const freePallets = hasUnmeasuredCargo ? 0 : freePalletsByNumbers;
  const occupiedPallets = safeCapacity > 0 ? safeCapacity - freePallets : round3(cappedLoaded);

  const view = {
    capacity: safeCapacity,
    loadedPallets: occupiedPallets,
    freePallets,
    loadPercent: loadPercentOf(occupiedPallets, safeCapacity),
    isFull: hasUnmeasuredCargo ? false : freePallets === 0,
    // Превышение вместимости — сигнал об ошибке в данных, а не нормальный режим.
    isOverloaded: safeCapacity > 0 && safeLoaded > safeCapacity,
    hasUnmeasuredCargo,
    loadedVolume: null,
    maxVolume: null,
    freeVolume: null,
  };

  // Объёмы показываем, только если объём поддона известен из готовых данных.
  // Кубатура остаётся фактической, а не подтянутой к целым местам: паллетоместо
  // занято целиком, но кубов в нём меньше, и это честно видно.
  if (palletVolume) {
    view.loadedVolume = round3(cappedLoaded * palletVolume);
    view.maxVolume = round3(safeCapacity * palletVolume);
    view.freeVolume = round3(freePallets * palletVolume);
  }

  return view;
};

/** Ниже этой загрузки рейс считается невыгодным. Ровно порог — ещё нормально. */
export const LIGHT_LOAD_PERCENT = 30;

/**
 * Машина загружена меньше чем на треть.
 *
 * Это не ошибка и не запрет: рейс уже оплачен, и в машине много места, которое
 * можно занять без дополнительного рейса. Признак нужен интерфейсу, чтобы
 * назвать это состояние и предложить дозагрузку, а не чтобы ругаться.
 *
 * Своих расчётов нет — читается уже посчитанное представление загрузки. Полная,
 * перегруженная и машина с неучтённым грузом сюда не попадают: у первых двух
 * вопрос не в загрузке, у третьей проценты недостоверны.
 *
 * @param {number}  loadPercent          — загрузка в процентах
 * @param {boolean} [isFull]             — машина закрыта
 * @param {boolean} [isOverloaded]       — вместимость превышена, ошибка данных
 * @param {boolean} [hasUnmeasuredCargo] — в машине груз без нормы поддона
 */
export const isLightlyLoadedHaul = ({
  loadPercent,
  isFull = false,
  isOverloaded = false,
  hasUnmeasuredCargo = false,
} = {}) => {
  if (isFull || isOverloaded || hasUnmeasuredCargo) {
    return false;
  }
  const percent = Number(loadPercent);
  if (!Number.isFinite(percent)) {
    return false;
  }
  return percent < LIGHT_LOAD_PERCENT;
};

export const WELL_LOADED_PERCENT = 75;

/**
 * Машина загружена хорошо: три четверти и больше.
 *
 * Полной она при этом не считается — свободное место ещё есть, и дозагрузить её
 * можно. Признак нужен интерфейсу, чтобы не пугать менеджера оранжевым там, где
 * рейс отработан почти целиком.
 *
 * Своих расчётов нет — читается уже посчитанное представление загрузки.
 *
 * @param {number}  loadPercent          — загрузка в процентах
 * @param {boolean} [isFull]             — машина закрыта, у неё своё состояние
 * @param {boolean} [isOverloaded]       — вместимость превышена, ошибка данных
 * @param {boolean} [hasUnmeasuredCargo] — в машине груз без нормы поддона
 */
export const isWellLoadedHaul = ({
  loadPercent,
  isFull = false,
  isOverloaded = false,
  hasUnmeasuredCargo = false,
} = {}) => {
  if (isFull || isOverloaded || hasUnmeasuredCargo) {
    return false;
  }
  const percent = Number(loadPercent);
  if (!Number.isFinite(percent)) {
    return false;
  }
  return percent >= WELL_LOADED_PERCENT;
};

export default buildGazVehicleLoadView;
